import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { GlowFrame } from './GlowFrame';
import { HONEYPOT_FIELD, submitFormRequest } from '../utils/formSubmission';
import type { FormFieldConfig } from '../types/contact';
interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  requestType: string;
  fields: FormFieldConfig[];
  initialValues?: Record<string, string>;
  submitLabel: string;
}
export function FormModal({
  isOpen,
  onClose,
  title,
  description,
  requestType,
  fields,
  initialValues = {},
  submitLabel
}: FormModalProps) {
  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [honeypot, setHoneypot] = useState('');
  const [formStartedAt, setFormStartedAt] = useState(Date.now());
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    setValues(initialValues);
    setHoneypot('');
    setFormStartedAt(Date.now());
    setSubmitted(false);
    setIsSubmitting(false);
    setError('');
    previouslyFocused.current = document.activeElement as HTMLElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      previouslyFocused.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const handleChange = (id: string, value: string) => {
    setValues(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await submitFormRequest({
        requestType,
        values,
        honeypot,
        formStartedAt
      });
      setSubmitted(true);
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : 'Your request could not be sent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <AnimatePresence data-id="element-87" data-mp="2xzmzj078s4yw">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-id="element-88" data-mp="2y0y4v4gc5ks4">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="absolute inset-0 bg-ink/90 backdrop-blur-md" onClick={onClose} aria-hidden="true" data-id="element-89" data-mp="321i2e94tmqes" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.97,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.97,
        y: 8
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} role="dialog" aria-modal="true" aria-label={title} className="relative z-10 max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto border border-led/20 bg-ink p-8 shadow-[0_0_0_1px_rgba(242,247,255,0.08),0_0_50px_-10px_rgba(143,211,240,0.35)]" data-id="element-90" data-mp="104g87q8mjg2f">
            <button ref={closeButtonRef} type="button" onClick={onClose} className="absolute right-5 top-5 text-led/60 transition-colors duration-300 hover:text-ice focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice" aria-label="Close form" data-id="element-91" data-mp="1a1lxcsxogx00">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-92" data-mp="3ps70a7k2o6yo" />
            </button>

            {submitted ? <div className="py-6 text-center" data-id="element-93" data-mp="1hiz9k6h3opl7">
                <h3 className="font-heading text-2xl uppercase tracking-tight text-led" data-id="element-94" data-mp="1mbk1t3kcyumm">Thank you</h3>
                <p className="mt-3 text-sm text-led/60" data-id="element-95" data-mp="18k5fonv5mdbc">
                  Your request has been received. We will be in touch shortly.
                </p>
              </div> : <>
                <h3 className="pr-8 font-heading text-2xl uppercase tracking-tight text-led" data-id="element-96" data-mp="20ajmk04r2pdi">{title}</h3>
                {description && <p className="mt-2 text-sm text-led/60" data-id="element-97" data-mp="259cs96dspc34">{description}</p>}
                <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate={false} data-id="element-98" data-mp="1rawmbhnrbpm1">
                  <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true" data-id="element-99" data-mp="1w8ez5j4waozb">
                    <label htmlFor={HONEYPOT_FIELD} data-id="element-100" data-mp="ftwzh1k6i2ze">Leave this field empty</label>
                    <input id={HONEYPOT_FIELD} name={HONEYPOT_FIELD} type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={event => setHoneypot(event.target.value)} data-id="element-101" data-mp="kub6qhdyh8wd" />
                  </div>

                  {fields.map(field => {
              const helperId = field.helperText ? `${field.id}-helper` : undefined;
              return <div key={field.id} data-id="element-102" data-mp="3ppufoeq7u2bl">
                        <label htmlFor={field.id} className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.2em] text-led/50" data-id="element-103" data-mp="3ktcbph2pm80b">
                          <span data-id="element-104" data-mp="3uoxa758kkq5b">{field.label}</span>
                          {field.helperText && <span id={helperId} className="normal-case tracking-normal text-led/35" data-id="element-105" data-mp="36z2xqos5x3ll">
                              {field.helperText}
                            </span>}
                        </label>
                        {field.type === 'textarea' ? <textarea id={field.id} required={field.required} placeholder={field.placeholder} maxLength={field.maxLength} aria-describedby={helperId} value={values[field.id] || ''} onChange={event => handleChange(field.id, event.target.value)} rows={3} className="w-full resize-none border border-led/20 bg-transparent px-3 py-2 text-sm text-led placeholder:text-led/30 focus:border-ice focus:outline-none" data-id="element-106" data-mp="1avy6b951ynbk" /> : <input id={field.id} type={field.type} required={field.required} placeholder={field.placeholder} maxLength={field.maxLength} aria-describedby={helperId} value={values[field.id] || ''} onChange={event => handleChange(field.id, event.target.value)} className="w-full border border-led/20 bg-transparent px-3 py-2 text-sm text-led placeholder:text-led/30 focus:border-ice focus:outline-none" data-id="element-107" data-mp="2ceqtcj6h48jf" />}
                      </div>;
            })}

                  {error && <p role="alert" className="text-sm text-ice" data-id="element-108" data-mp="1ptzkjvo1f0f3">
                      {error}
                    </p>}

                  <GlowFrame as="button" type="submit" interactive disabled={isSubmitting} className="mt-2 w-full px-6 py-3 text-center text-xs uppercase tracking-[0.2em] text-led disabled:cursor-wait disabled:opacity-50" data-id="element-109" data-mp="3o6823gxm2yt0">
                    {isSubmitting ? 'Sending…' : submitLabel}
                  </GlowFrame>
                </form>
              </>}
          </motion.div>
        </div>}
    </AnimatePresence>;
}